import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';

import {useSelector} from 'react-redux';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useGetBusinessCaseQuery,
  useGetGeneralSettingsQuery,
  useGetVehicleV2Query,
  useListVehicleCampaignsQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {selectSaleVehicle, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {getNewVehicleDeliveryFlag} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  EntityHeaderFlag,
  getRentalOrderStateFlag,
  getVehicleAdvertisementTypeFlagProps,
  getVehicleBuyingStateFlagProps,
  getVehiclePhotographedFlagProps,
  getVehicleSaleTypeFlagProps,
  getVehicleSellingStateFlagProps,
  RentalOrderState,
  useIsLicenceEnabled,
  usePermissions,
  useVehiclePhotosPermissions,
} from '@dms/shared';

import {buildArray} from 'shared/web';

import {getActiveRentalOrder} from '../utils/getActiveRentalOrder';

// eslint-disable-next-line complexity -- Pre-existing violation
export const useGetVehicleHeaderFlags = (vehicleId: string) => {
  const {data: vehicle} = useGetVehicleV2Query({vehicleId});
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId});
  const {data: saleVehicle} = useSelector(selectSaleVehicle(vehicleId));
  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId});
  const {data: vehicleCampaigns} = useListVehicleCampaignsQuery(
    isFeatureEnabled(featureFlags.CAMPAIGNS) ? {vehicleId} : skipToken
  );

  const saleTypeFlagProps = getVehicleSaleTypeFlagProps(saleVehicle?.type);
  const sellingStateFlagProps = getVehicleSellingStateFlagProps(saleVehicle?.sellingState);
  const buyingStateFlagProps = getVehicleBuyingStateFlagProps(saleVehicle?.buyingState);
  const advertisementStateFlagProps = getVehicleAdvertisementTypeFlagProps(generalSettings?.inSale);
  const photographedFlagProps = getVehiclePhotographedFlagProps(saleVehicle?.isPhotographed);

  const activeRentalOrder = getActiveRentalOrder(rentalVehicle);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: saleVehicle?.saleBusinessCaseId ?? '',
    },
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const [
    canReadReservations,
    canReadReservationsHistory,
    hasReadAdvertisingSettingsPermission,
    canReadBusinessCase,
    canReadRentalVehicle,
    canReadSellingBusinessCase,
    canReadBuyingBusinessCase,
  ] = usePermissions({
    permissionKeys: [
      'vehicleReadActiveReservation',
      'vehicleReadReservationHistory',
      'readAdvertisingSettings',
      'businessCaseRead',
      'readRentalVehicle',
      'viewBusinessCaseSelling',
      'viewBusinessCasePurchase',
    ],
    scopes: {
      vehicleReadActiveReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReadReservationHistory: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      viewBusinessCasePurchase: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId,
  });

  // Stands in for the sale type while the ordered car has not arrived. Claiming "In stock" for a
  // vehicle still in transit - or one whose order was cancelled and is never coming - is the one
  // thing the header must not do; the grids suppress it the same way (D8/D53). The sale vehicle
  // carries the derived arrival facts, so this costs no extra request.
  const deliveryFlag = getNewVehicleDeliveryFlag(saleVehicle);

  const {isAdvertisingEnabled} = useIsLicenceEnabled();

  const isSalesModuleEnabled = canReadBusinessCase;

  const shouldDisplaySellingStateFlag =
    isSalesModuleEnabled &&
    canReadSellingBusinessCase &&
    saleVehicle?.sellingState &&
    isNotNil(sellingStateFlagProps);

  const shouldDisplayBuyingStateFlag =
    isSalesModuleEnabled &&
    canReadBuyingBusinessCase &&
    saleVehicle?.buyingState &&
    isNotNil(buyingStateFlagProps);

  const shouldDisplayAdvertisementFlag =
    isSalesModuleEnabled &&
    isAdvertisingEnabled &&
    hasReadAdvertisingSettingsPermission &&
    (sellingStateFlagProps || buyingStateFlagProps) &&
    isNotNil(advertisementStateFlagProps);

  const shouldDisplayPhotographedFlag =
    canReadVehicleAlbumPhotos && isNotNil(photographedFlagProps) && isSalesModuleEnabled;

  const shouldDisplayReservedFlag =
    saleVehicle?.activeReservation &&
    canReadReservations &&
    canReadReservationsHistory &&
    isSalesModuleEnabled;

  const shouldDisplayCampaignFlag =
    isSalesModuleEnabled &&
    isNotNil(saleVehicle) &&
    Boolean(
      vehicleCampaigns?.some((campaign) => campaign.status === 'ACTIVE' && campaign.isApplied)
    );

  const rentalOrderStateFlag = getRentalOrderStateFlag(
    (activeRentalOrder?.state ?? null) as RentalOrderState
  );

  return buildArray<EntityHeaderFlag>()
    .when(isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) && rentalVehicle?.active, {
      label: i18n.t('entity.vehicle.labels.rentalActive'),
      colorScheme: 'green',
      'data-testid': testIds.vehicles.detail('vehicleHeader-rental'),
    })
    .when(
      isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) &&
        isNotNil(rentalVehicle) &&
        isNotNil(activeRentalOrder?.state) &&
        canReadRentalVehicle,
      rentalOrderStateFlag
    )
    .when(shouldDisplayReservedFlag, {
      label: i18n.t('entity.vehicle.labels.reserved'),
      colorScheme: 'yellow',
      'data-testid': testIds.vehicles.detail('vehicleHeader-reserved'),
    })
    .when(vehicle?.isArchived, {
      label: i18n.t('entity.vehicle.labels.archived'),
      colorScheme: 'neutral',
      'data-testid': testIds.vehicles.detail('vehicleHeader-archived'),
    })
    .when(isNotNil(deliveryFlag) && !vehicle?.isArchived, {
      label: deliveryFlag?.label ?? '',
      colorScheme: deliveryFlag?.colorScheme ?? 'blue',
      'data-testid': testIds.vehicles.detail('vehicleHeader-newVehicleDelivery'),
    })
    .when(
      isNil(deliveryFlag) &&
        isNotNil(saleTypeFlagProps) &&
        !vehicle?.isArchived &&
        isSalesModuleEnabled,
      saleTypeFlagProps!
    )
    .when(shouldDisplaySellingStateFlag, sellingStateFlagProps!)
    .when(shouldDisplayBuyingStateFlag, buyingStateFlagProps!)
    .when(shouldDisplayAdvertisementFlag, advertisementStateFlagProps!)
    .when(shouldDisplayPhotographedFlag, photographedFlagProps!)
    .when(shouldDisplayCampaignFlag, {
      label: i18n.t('entity.salesCampaign.labels.campaign'),
      colorScheme: 'yellow',
      'data-testid': testIds.vehicles.detail('vehicleHeader-campaign'),
    });
};
